import { useEffect, useRef, useState } from "react";

/** Returns [ref, inView] — sets inView=true once when entering viewport. */
export function useInView(options = { threshold: 0.18, once: true }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      const raf = requestAnimationFrame(() => setInView(true));
      return () => cancelAnimationFrame(raf);
    }
    if (typeof IntersectionObserver === "undefined") {
      const raf = requestAnimationFrame(() => setInView(true));
      return () => cancelAnimationFrame(raf);
    }

    let raf = null;
    const setLater = (v) => {
      raf = requestAnimationFrame(() => setInView(v));
    };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setLater(true);
          if (options.once) obs.disconnect();
        } else if (!options.once) {
          setLater(false);
        }
      });
    }, { threshold: options.threshold ?? 0.18, rootMargin: options.rootMargin ?? "0px" });

    obs.observe(node);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [options.threshold, options.once, options.rootMargin]);

  return [ref, inView];
}

/** Animates a number from 0 to `to` once when `start` becomes true. */
export function useCountUp(to, start, duration = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return undefined;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, start, duration]);

  return value;
}
